// src/features/live-board/components/DeviceStrip.tsx
//
// «Device strip on top that turns grey, not red, on silence» (CF S6).
// `board.device_strip` (`V2BoardFinItMetricDefinitions.BoardDeviceStrip`)
// states each device's `health` in the v2 vocabulary (01-domain-model.md
// §3.7): `up`, `degraded`, `down` (a stated blocking fault only),
// `maintenance`, `blocked`, and `unknown` — silence beyond the heartbeat
// interval, with a `since` timestamp, "never `down`". Only a stated `down`
// is red; silence and any value this screen does not know are grey.
import React from 'react';
import { Box, Chip, Skeleton, Tooltip } from '@mui/material';
import { useLocale, useTranslate } from 'react-admin';
import { DateTimeSystem } from '@/utils/dateTimeSystem';
import { WidgetCard } from '@/components/common/WidgetCard';
import { MetricStamp } from './MetricStamp';
import type { BoardRow, MetricResultDto } from '@/types/analyticsV2';

interface DeviceStripProps {
  metric: MetricResultDto | undefined;
  loading: boolean;
}

type ChipColor = 'default' | 'success' | 'warning' | 'error';

const HEALTH_COLOR: Record<string, ChipColor> = {
  up: 'success',
  degraded: 'warning',
  maintenance: 'warning',
  blocked: 'warning',
  down: 'error',
};

/** Grey (`default`) for `unknown` and for anything not in the vocabulary. */
export const deviceHealthColor = (health: unknown): ChipColor =>
  (typeof health === 'string' && HEALTH_COLOR[health]) || 'default';

export const DeviceStrip: React.FC<DeviceStripProps> = ({ metric, loading }) => {
  const translate = useTranslate();
  const locale = useLocale();
  const rows = metric && !metric.absent && Array.isArray(metric.value) ? (metric.value as BoardRow[]) : [];

  return (
    <WidgetCard title={translate('resources.liveBoard.deviceStrip.title')}>
      {loading ? (
        <Skeleton height={40} />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {rows.length === 0 && (
            <Chip size="small" variant="outlined" label={translate('resources.liveBoard.deviceStrip.empty')} />
          )}
          {rows.map((row, i) => {
            const label = (row.label as string) ?? (row.device_id as string) ?? `#${i}`;
            const since = row.since as string | null | undefined;
            const color = deviceHealthColor(row.health);
            const grey = color === 'default';
            const health = typeof row.health === 'string' ? row.health : 'unknown';
            return (
              <Tooltip
                key={(row.device_id as string) ?? i}
                title={translate(`resources.liveBoard.deviceStrip.health.${health}`, {
                  _: translate('resources.liveBoard.deviceStrip.health.unknown'),
                })}
              >
                <Chip
                  size="small"
                  color={color}
                  variant={grey ? 'outlined' : 'filled'}
                  sx={grey ? { bgcolor: 'action.disabledBackground', color: 'text.secondary' } : undefined}
                  label={
                    grey
                      ? since
                        ? translate('resources.liveBoard.deviceStrip.silentSince', { relative: DateTimeSystem.formatRelative(since, locale), label })
                        : translate('resources.liveBoard.deviceStrip.silent', { label })
                      : label
                  }
                />
              </Tooltip>
            );
          })}
        </Box>
      )}
      {metric && <MetricStamp stamp={metric.stamp} />}
    </WidgetCard>
  );
};

export default DeviceStrip;
